/**
 * Warehouse-vs-database comparison, via /api/bq-reconcile.
 *
 * Reading BigQuery needs the Google service-account key, which is server-side
 * only, so this goes through the endpoint rather than talking to BigQuery
 * directly — the same shape as bigQueryJobService.
 *
 * @module services/bigQueryReconcileService
 */

import { getSupabaseClient } from "@/config/supabase";
import { logError } from "@/services/logger";

export interface SideTotals {
  platform: string;
  campaignDays: number;
  cost: number;
  impressions: number;
  clicks: number;
  conversions: number;
}

export interface PlatformComparison {
  platform: string;
  /** null when the platform is absent from that side entirely. */
  warehouse: SideTotals | null;
  source: SideTotals | null;
  /** source − warehouse; all zero is the healthy state. */
  delta: {
    campaignDays: number;
    cost: number;
    impressions: number;
    clicks: number;
    conversions: number;
  };
  matches: boolean;
}

export interface ReconcileWindow {
  requestedFrom: string | null;
  requestedTo: string | null;
  /** What was actually compared, after clamping to what the warehouse holds. */
  from: string | null;
  to: string | null;
  warehouseFrom: string | null;
  warehouseTo: string | null;
  /** Reached back past what the warehouse holds — the 60-day expiry; data is missing. */
  clampedStart: boolean;
  /** Reached past the warehouse's latest date — ordinary freshness lag, not loss. */
  clampedEnd: boolean;
}

export interface ReconcileReport {
  /** false when BQ_PROJECT / the service-account key are unset — not an error. */
  configured: boolean;
  workspace: { id: string; name: string; dataset: string } | null;
  window: ReconcileWindow | null;
  platforms: PlatformComparison[];
  /** The warehouse holds nothing for this workspace at all. */
  empty: boolean;
}

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export const fetchReconcileReport = async (input: {
  workspaceId: string;
  from?: string;
  to?: string;
}): Promise<ServiceResult<ReconcileReport>> => {
  try {
    const { data: session } = await getSupabaseClient().auth.getSession();
    const token = session.session?.access_token;
    if (!token) return { success: false, error: "Not signed in" };

    const params = new URLSearchParams({ workspace_id: input.workspaceId });
    if (input.from) params.set("from", input.from);
    if (input.to) params.set("to", input.to);

    const resp = await fetch(`/api/bq-reconcile?${params.toString()}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = (await resp.json().catch(() => null)) as
      | (Partial<ReconcileReport> & { error?: string })
      | null;
    if (!resp.ok) {
      return { success: false, error: data?.error ?? "Could not compare the warehouse" };
    }

    return {
      success: true,
      data: {
        configured: data?.configured === true,
        workspace: data?.workspace ?? null,
        window: data?.window ?? null,
        platforms: data?.platforms ?? [],
        empty: data?.empty === true,
      },
    };
  } catch (e) {
    logError("BigQueryReconcile", "fetch failed", e);
    return {
      success: false,
      error: e instanceof Error ? e.message : "Could not compare the warehouse",
    };
  }
};
